import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import type { FormEvent } from 'react';
import { useMemo, useRef, useState } from 'react';

import type { ProductMessage } from '../../../../types/ProductMessage';
import type { ProductReferenceConflictResponse } from '../../../../types/ProductReferenceMessage';
import { Accordion } from '../../../cdl/Accordion/Accordion';
import { Button } from '../../../cdl/Button/Button';
import { Callout } from '../../../cdl/Callout/Callout';
import { Checkbox } from '../../../cdl/Checkbox/Checkbox';
import type { ComboBoxOption } from '../../../cdl/ComboBox/ComboBox';
import { ComboBox, PaginatedAsyncComboBox } from '../../../cdl/ComboBox/ComboBox';
import { DrawerContentLayout } from '../../../cdl/Drawer/DrawerContentLayout';
import { useDrawer } from '../../../cdl/Drawer/useDrawer.hook';
import { useToasts } from '../../../cdl/Toast/useToasts';
import { getProductsByIds, updateProductReferences } from '../../../common/api/clients/product.api';
import { queries } from '../../../common/api/queryKeys/queries';
import { translate } from '../../../common/helpers/translate.helper';
import { useEnums } from '../../../common/hooks/useEnums';
import { IconCornerDownRight } from '../../../common/icons/cdl/CornerDownRight';
import { IconPlus } from '../../../common/icons/cdl/Plus';
import { LoadingIndicator } from '../../../common/LoadingIndicator/LoadingIndicator';
import { Box } from '../../../common/ui/Box';
import { useLoadProductsForSupplierGroup } from '../hooks/useLoadProductsForSupplierGroup';

import { SupplierGroupOption } from './SupplierGroupOption';
import { SupplierGroupSingleValue } from './SupplierGroupSingleValue';

interface EquivalentEntry {
    id: string;
    supplierProductGroup: string;
    productId: string;
    productName: string | null;
    bidirectional: boolean;
}

interface ManageEquivalentsDrawerProps {
    product: ProductMessage;
}

const getReferenceConflict = (error: unknown): ProductReferenceConflictResponse | null => {
    const axiosError = error as AxiosError<ProductReferenceConflictResponse>;
    if (axiosError.response?.status === 409 && axiosError.response.data?.code === 'REFERENCE_CONFLICT') {
        return axiosError.response.data;
    }
    return null;
};

export const ManageEquivalentsDrawer = ({ product }: ManageEquivalentsDrawerProps) => {
    const { pop, layerId } = useDrawer();
    const { addToast, addErrorToast } = useToasts();
    const { getHumanReadableValue, getEnumsForTypeAndContext } = useEnums();
    const queryClient = useQueryClient();
    const entryCounter = useRef(0);

    const referencesQuery = useQuery({
        ...queries.products.references(product.id),
        staleTime: 0,
    });

    const remoteEntries = useMemo<EquivalentEntry[]>(() => {
        if (!referencesQuery.data) {
            return [];
        }
        return referencesQuery.data.references.map((reference) => ({
            id: `existing-${reference.supplierProductGroup}`,
            supplierProductGroup: reference.supplierProductGroup,
            productId: reference.productId,
            productName: reference.productName,
            bidirectional: reference.bidirectional,
        }));
    }, [referencesQuery.data]);

    const [draftEntries, setDraftEntries] = useState<EquivalentEntry[] | null>(null);
    const [expandedItem, setExpandedItem] = useState<string | undefined>(undefined);
    const [conflictMessage, setConflictMessage] = useState<string | null>(null);

    const currentEntries = draftEntries ?? remoteEntries;

    const loadProductsForSupplierGroup = useLoadProductsForSupplierGroup(product.id);

    const loadSelectedProduct = async (productId: string): Promise<ComboBoxOption | null> => {
        const selectedProduct = (await getProductsByIds([productId]))[0];

        return selectedProduct ? { value: selectedProduct.id, label: selectedProduct.name } : null;
    };

    const updateMutation = useMutation({
        mutationFn: updateProductReferences,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queries.products.references._def });
            queryClient.invalidateQueries({ queryKey: queries.products.pagination._def });
        },
    });

    const hasChanges = useMemo(() => {
        if (draftEntries === null) {
            return false;
        }
        if (draftEntries.length !== remoteEntries.length) {
            return true;
        }
        return draftEntries.some((entry) => {
            const original = remoteEntries.find((item) => item.supplierProductGroup === entry.supplierProductGroup);
            if (!original) {
                return true;
            }
            return original.productId !== entry.productId || original.bidirectional !== entry.bidirectional;
        });
    }, [draftEntries, remoteEntries]);

    const handleSave = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setConflictMessage(null);
        updateMutation.mutate(
            {
                productId: product.id,
                references: currentEntries
                    .filter((entry) => entry.supplierProductGroup && entry.productId)
                    .map((entry) => ({
                        supplierProductGroup: entry.supplierProductGroup,
                        productId: entry.productId,
                        bidirectional: entry.bidirectional,
                    })),
            },
            {
                onSuccess: () => {
                    addToast(translate('productsOverview.equivalents.successToast'));
                    pop(layerId);
                },
                onError: async (error) => {
                    const conflict = getReferenceConflict(error);
                    if (!conflict) {
                        addErrorToast(translate('productsOverview.equivalents.errorToast'));
                        return;
                    }
                    try {
                        const products = await getProductsByIds([conflict.refProductId, conflict.existingInverse]);
                        const nameById = new Map(products.map((item) => [item.id, item.name]));
                        addErrorToast(translate('productsOverview.equivalents.conflictToast'));
                        setConflictMessage(
                            translate('productsOverview.equivalents.conflictCalloutDescription', {
                                refProduct: nameById.get(conflict.refProductId) ?? conflict.refProductId,
                                existingProduct: nameById.get(conflict.existingInverse) ?? conflict.existingInverse,
                                supplierGroup: getHumanReadableValue('supplierProductGroup', conflict.supplierGroupKey),
                            })
                        );
                    } catch {
                        addErrorToast(translate('productsOverview.equivalents.errorToast'));
                    }
                },
            }
        );
    };

    const handleAddEntry = () => {
        const newId = `new-${entryCounter.current++}`;
        setDraftEntries([
            ...currentEntries,
            {
                id: newId,
                supplierProductGroup: '',
                productId: '',
                productName: null,
                bidirectional: false,
            },
        ]);
        setExpandedItem(newId);
    };

    const handleRemoveEntry = (id: string) => {
        setDraftEntries(currentEntries.filter((entry) => entry.id !== id));
        if (expandedItem === id) {
            setExpandedItem(undefined);
        }
    };

    const handleSupplierGroupChange = (id: string, value: string) => {
        setDraftEntries(
            currentEntries.map((entry) => {
                if (entry.id !== id) {
                    return entry;
                }
                return { ...entry, supplierProductGroup: value, productId: '', productName: null };
            })
        );
    };

    const handleProductChange = (id: string, option: ComboBoxOption | null) => {
        setDraftEntries(
            currentEntries.map((entry) => {
                if (entry.id !== id) {
                    return entry;
                }
                return {
                    ...entry,
                    productId: option?.value ?? '',
                    productName: option?.label ?? null,
                };
            })
        );
    };

    const handleBidirectionalChange = (id: string, checked: boolean) => {
        setDraftEntries(
            currentEntries.map((entry) => {
                if (entry.id !== id) {
                    return entry;
                }
                return { ...entry, bidirectional: checked };
            })
        );
    };

    const usedSupplierGroups = new Set([
        product.supplierProductGroup,
        ...currentEntries.map((entry) => entry.supplierProductGroup).filter(Boolean),
    ]);

    const getEntryTitle = (entry: EquivalentEntry): string => {
        if (!entry.supplierProductGroup) {
            return translate('productsOverview.equivalents.newEquivalent');
        }
        return getHumanReadableValue('supplierProductGroup', entry.supplierProductGroup);
    };

    const getAvailableSupplierGroups = (currentGroup: string): ComboBoxOption[] => {
        return Object.keys(getEnumsForTypeAndContext('supplierProductGroup') ?? {})
            .filter((key) => key === currentGroup || !usedSupplierGroups.has(key))
            .map((key) => ({
                value: key,
                label: getHumanReadableValue('supplierProductGroup', key),
            }));
    };

    return (
        <form style={{ height: '100%', minHeight: 0 }} onSubmit={handleSave}>
            <DrawerContentLayout
                isBusy={updateMutation.isPending}
                hasUnsavedChanges={hasChanges}
                primaryAction={
                    <Button type="submit" emphasis="high" isLoading={updateMutation.isPending} disabled={!hasChanges}>
                        {translate('productsOverview.equivalents.save')}
                    </Button>
                }
            >
                {conflictMessage ? (
                    <Callout
                        variant="negative"
                        heading={translate('productsOverview.equivalents.conflictCalloutHeading')}
                        description={conflictMessage}
                        onDismiss={() => setConflictMessage(null)}
                    />
                ) : null}

                <Callout
                    description={`${getHumanReadableValue('supplierProductGroup', product.supplierProductGroup)} · ${product.name}`}
                />

                {referencesQuery.isPending ? (
                    <LoadingIndicator />
                ) : (
                    <>
                        <Accordion value={expandedItem} onValueChange={setExpandedItem}>
                            {currentEntries.map((entry) => (
                                <Accordion.Item
                                    key={entry.id}
                                    id={entry.id}
                                    title={getEntryTitle(entry)}
                                    summary={entry.productName ?? undefined}
                                    onRemove={() => handleRemoveEntry(entry.id)}
                                >
                                    <Box display="flex" flexDirection="column" rowGap={12} paddingY={8}>
                                        <ComboBox
                                            value={entry.supplierProductGroup || null}
                                            onChange={(value) => handleSupplierGroupChange(entry.id, value ?? '')}
                                            options={getAvailableSupplierGroups(entry.supplierProductGroup)}
                                            placeholder={translate(
                                                'productsOverview.equivalents.supplierGroupPlaceholder'
                                            )}
                                            OptionComponent={SupplierGroupOption}
                                            ValueComponent={SupplierGroupSingleValue}
                                        />

                                        {entry.supplierProductGroup ? (
                                            <Box display="flex" alignItems="flex-start" gap={8}>
                                                <Box paddingTop={10} color="foreground.subtle">
                                                    <IconCornerDownRight width={16} height={16} />
                                                </Box>
                                                <Box flex={1}>
                                                    <PaginatedAsyncComboBox
                                                        value={entry.productId || null}
                                                        onChange={(_, meta) =>
                                                            handleProductChange(entry.id, meta.option)
                                                        }
                                                        optionsQueryKey={(search) =>
                                                            queries.products.equivalentComboBoxOptions({
                                                                excludedProductId: product.id,
                                                                searchQuery: search,
                                                                supplierProductGroup: entry.supplierProductGroup,
                                                            }).queryKey
                                                        }
                                                        optionsQueryFn={({ search, page }) =>
                                                            loadProductsForSupplierGroup(
                                                                entry.supplierProductGroup,
                                                                search,
                                                                page
                                                            )
                                                        }
                                                        selectedOptionQueryKey={(id) =>
                                                            queries.products.selectedComboBoxOption({ productId: id })
                                                                .queryKey
                                                        }
                                                        selectedOptionQueryFn={loadSelectedProduct}
                                                        placeholder={translate(
                                                            'productsOverview.equivalents.productPlaceholder'
                                                        )}
                                                    />
                                                </Box>
                                            </Box>
                                        ) : null}

                                        {entry.productId ? (
                                            <Box paddingLeft={24}>
                                                <Checkbox
                                                    label={translate('productsOverview.equivalents.linkBidirectional')}
                                                    checked={entry.bidirectional}
                                                    onChange={(event) => {
                                                        handleBidirectionalChange(entry.id, event.target.checked);
                                                    }}
                                                />
                                            </Box>
                                        ) : null}
                                    </Box>
                                </Accordion.Item>
                            ))}
                        </Accordion>

                        <Box>
                            <Button type="button" emphasis="low" onClick={handleAddEntry} LeadingIcon={IconPlus}>
                                {translate('productsOverview.equivalents.addEquivalent')}
                            </Button>
                        </Box>
                    </>
                )}
            </DrawerContentLayout>
        </form>
    );
};
